import { MouseEvent, useState, useRef, useEffect } from 'react'
import { useContent } from './ContentContext'
import { Button, classNames } from '@cma/common'

const VISIBLE_LINES = 3

interface RenameInputProps {
  defaultValue: string
  variant?: 'sm' | 'md'
  error?: boolean
  saveButtonPosition?: 'overlay' | 'inline'
  onSubmit: (value: string) => void
  onCancel: () => void
  onChange?: (value: string) => void
}

export function RenameInput({
  variant = 'sm',
  defaultValue,
  error = false,
  saveButtonPosition = 'overlay',
  onSubmit,
  onCancel
}: RenameInputProps) {
  const [value, setValue] = useState(defaultValue)
  const [hasError, setHasError] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const { setIsRenaming } = useContent()

  const resize = (el: HTMLTextAreaElement) => {
    const computed = window.getComputedStyle(el)
    const lineHeight = parseFloat(computed.lineHeight) || 16
    const paddingY =
      parseFloat(computed.paddingTop) + parseFloat(computed.paddingBottom)
    const borderY =
      parseFloat(computed.borderTopWidth) +
      parseFloat(computed.borderBottomWidth)
    const maxHeight = lineHeight * VISIBLE_LINES + paddingY + borderY

    el.style.height = 'auto'
    const scrollHeight = el.scrollHeight
    el.style.height = `${Math.min(scrollHeight, maxHeight)}px`
    el.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden'
  }

  useEffect(() => {
    if (textareaRef.current) {
      setTimeout(() => {
        if (!textareaRef.current) {
          return
        }
        textareaRef.current.focus()
        const length = textareaRef.current.value.length
        textareaRef.current.setSelectionRange(length, length)
        resize(textareaRef.current)
      }, 10)
    }

    return () => {
      setIsRenaming(false)
    }
  }, [setIsRenaming])

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value
    setValue(newValue)
    setHasError(newValue.trim() === '')
  }

  const submit = () => {
    if (!value.trim()) {
      setHasError(true)
      return
    }
    onSubmit(value)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      submit()
    }
  }

  useEffect(() => {
    function onKeyPress(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault()
        onCancel()
      }
    }

    document.body.addEventListener('keydown', onKeyPress)
    return () => {
      document.body.removeEventListener('keydown', onKeyPress)
    }
  }, [onCancel])

  return (
    <div
      className={classNames('w-full', {
        'flex flex-col items-end gap-1': saveButtonPosition === 'overlay',
        'flex items-start gap-2': saveButtonPosition === 'inline'
      })}>
      <textarea
        ref={textareaRef}
        value={value}
        rows={1}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onClick={(e) => {
          e.stopPropagation()
        }}
        onInput={(e) => resize(e.target as HTMLTextAreaElement)}
        className={classNames(
          'min-h-[24px] w-full resize-none overflow-x-hidden !border !border-solid px-2 py-1 selection:bg-brand-white focus:outline-none',
          {
            '!border-red-500 focus:!border-red-500 focus:ring-red-500':
              hasError || error,
            '!border-gray-300 focus:!border-brand-primary focus:ring-brand-primary':
              !hasError && !error,
            '!text-sm': variant === 'md',
            '!text-xs': variant === 'sm',
            'min-w-0 flex-1': saveButtonPosition === 'inline'
          }
        )}
      />
      <span className="flex-shrink-0">
        <SaveButton
          disabled={hasError || error || !value.trim()}
          onClick={submit}
        />
      </span>
    </div>
  )
}

function SaveButton({
  disabled,
  onClick
}: {
  disabled: boolean
  onClick: () => void
}) {
  return (
    <Button
      type="button"
      variant="primary"
      size="xxs"
      disabled={disabled}
      onClick={(e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation()
        onClick()
      }}>
      Save
    </Button>
  )
}
